import axios from 'axios';
import {ApiError} from '@/common/errors/api-error';
import appModule from '@/model/store/modules/app-module';
import userModule from '@/model/store/modules/user-module';
import {GlobalConstants} from '@/common/constants';
import {PLDataReq, PostLoginReq} from '@/model/dto/req/login/post-login-req';
import {GetLoginInfoRes} from '@/model/dto/res/login/get-login-info-res';
import {GetUserInfoRes} from '@/model/dto/res/user/get-user-info-res';
import {GCICategoryInfoRes, GCIContentsInfoRes, GCIContentsListRes, GCICategoryManageInfoRes} from '@/model/dto/res/contents/get-category-list-res';
import {GCIAboutRes, GCIAboutInfoRes} from '@/model/dto/res/about/get-about-info-res';
import {GCIHeadlineRes, GCIHeadlineInfoRes} from '@/model/dto/res/headline/get-headline-info-res';
import {GCIMenuRes, GCIMenuInfoRes} from '@/model/dto/res/menu/get-menu-info-res';
import {GCIInfomationRes, GCIInfomationInfoRes} from '@/model/dto/res/infomation/get-infomation-info-res';
import {PUCATDataReq, PostUpsertCategoryReq} from '@/model/dto/req/category/post-upsert-category-req';
import {SearchQueryInterface} from '@/model/interfaces/search-interface';
import {PUCDataReq, PostUpsertContentsReq} from '@/model/dto/req/contents/post-upsert-contents-req';
import {PostUpsertContentsRes, PostUpsertContentsInfoRes} from '@/model/dto/res/contents/post-upsert-contents-res';
import {PUICDataReq, PostUploadImageContentsReq} from '@/model/dto/req/contents/post-upload-image-contents-req';
import {PostUploadImageContentsRes, PostUploadImageContentsInfoRes} from '@/model/dto/res/contents/post-upload-image-contents-res';
import {PDCDataReq, PostDeleteContentsReq} from '@/model/dto/req/contents/post-delete-contents-req';
import {PostDeleteContentsRes, PostDeleteContentsInfoRes} from '@/model/dto/res/contents/post-delete-contents-res';
import {PUADataReq, PostUpsertAboutReq} from '@/model/dto/req/about/post-upsert-about-req';
import {PostUpsertAboutRes, PostUpsertAboutInfoRes} from '@/model/dto/res/about/post-upsert-about-res';
import {PUIADataReq, PostUploadImageAboutReq} from '@/model/dto/req/about/post-upload-image-about-req';
import {PostUploadImageAboutRes, PostUploadImageAboutInfoRes} from '@/model/dto/res/about/post-upload-image-about-res';
import {PUHDataReq, PostUpsertHeadlineReq} from '@/model/dto/req/headline/post-upsert-headline-req';
import {PostUpsertHeadlineRes, PostUpsertHeadlineInfoRes} from '@/model/dto/res/headline/post-upsert-headline-res';
import {PUMDataReq, PostUpsertMenuReq} from '@/model/dto/req/menu/post-upsert-menu-req';
import {PostUpsertMenuRes, PostUpsertMenuInfoRes} from '@/model/dto/res/menu/post-upsert-menu-res';
import {PUIDataReq, PostUpsertInfomationReq} from '@/model/dto/req/infomation/post-upsert-infomation-req';
import {PostUpsertInfomationRes, PostUpsertInfomationInfoRes} from '@/model/dto/res/infomation/post-upsert-infomation-res';
import {GCIHeaderRes, GCIHeaderInfoRes, GCIHeaderListsInfoRes} from '@/model/dto/res/header/get-header-info-res';
import {PUIHDataReq, PostUpsertHeaderReq} from '@/model/dto/req/header/post-upsert-header-req';
import {GULUserRes, GetUserListRes} from '@/model/dto/res/user/get-user-list-res';
import {PUUDataReq, PostUpsertUserReq} from '@/model/dto/req/user/post-upsert-user-req';
import {PostUpsertUserRes, PostUpsertUserInfoRes} from '@/model/dto/res/user/post-upsert-user-res';
import {GCIHolidayRes, GCIHolidayInfoRes} from '@/model/dto/res/holiday/get-holiday-info-res';
import {PostUpsertHolidayRes, PostUpsertHolidayInfoRes} from '@/model/dto/res/holiday/post-upsert-holiday-res';
import {PUHDDataReq, PostUpsertHolidayReq} from '@/model/dto/req/holiday/post-upsert-holiday-req';
import qs from 'qs';

class Api {

  private timeout = { timeout: 10000 };

  /**
   * axiosリクエストのconfig取得
   */
  private getConfig(
    authToken: string,
    crsfTokenName?: string,
    crsfToken?: string,
    image?: boolean
  ) {
    if (!crsfTokenName) {
      return {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      };
    } else {
      if (image) {
        return {
          headers: {
            'Content-Type': 'multipart/form-data',
            'enctype': 'multipart/form-data',
            'Accept': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            [`${crsfTokenName ? crsfTokenName : ''}`]: crsfToken ? crsfToken : ''
          }
        };
      } else {
        return {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            [`${crsfTokenName ? crsfTokenName : ''}`]: crsfToken ? crsfToken : ''
          }
        };
      }

    }
  }

  /**
   * トークンの更新
   *
   * @param token
   */
  private updateToken(token: string) {
    appModule.updateToken({
      token
    });
  }

  /**
   * コンテンツ情報削除
   *
   * @param {{contentId: string}} data
   * @return {PostUpsertContentsRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postLoginInfo(
    data: PLDataReq
  ): Promise<GetLoginInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/login`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostLoginReq(new PLDataReq(
        data.userId,
        data.password
      ));
      const response = await axios.post(url, req, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as GetLoginInfoRes;
  }

  /**
   * コンテンツ情報削除
   *
   * @param {{contentId: string}} data
   * @return {PostUpsertContentsRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postLogoutInfo(): Promise<GetLoginInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/logout`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const response = await axios.post(url, {}, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as GetLoginInfoRes;
  }

  /**
   * ユーザ情報取得
   *
   * @return {GetUserInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getUserInfo(): Promise<GetUserInfoRes> {
    const url = `${process.env.VUE_APP_API_BASE_URL}/user/me`;
    const config = this.getConfig(appModule.token);
    const response = await axios.get(url, config).catch(err => {
      console.log(err);
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GetUserInfoRes;
  }

  /**
   * ユーザ一覧情報取得
   *
   * @return {GCIMenuInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getUserListInfo(id?: string): Promise<GetUserListRes> {
    const url = `${process.env.VUE_APP_API_BASE_URL}/user/list`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    // const config = this.getConfig(token);
    const config = Object.assign(this.getConfig(token), { params: {
      id,
    }});
    const response = await axios.get(url, {...config}).catch(err => {
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message, err.response?.status);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GetUserListRes;
  }

  /**
   * ユーザ情報更新
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {PostUpsertContentsRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUpsertUserInfo(
    data: PUUDataReq
  ): Promise<PostUpsertUserInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/user/save`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostUpsertUserReq(new PUUDataReq(
        data.id,
        data.name,
        data.type,
        data.password,
        data.email
      ));
      const response = await axios.post(url, req, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as PostUpsertUserInfoRes;
  }

  /**
   * カテゴリ情報取得
   *
   * @return {GCICategoryInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getCategoryInfo(param: SearchQueryInterface ): Promise<GCICategoryInfoRes> {
    const c = param.c;
    const q = param.q;
    const s = param.s;
    const n = param.n;
    const o = param.o;
    const categoryType = param.categoryType;
    const url = `${process.env.VUE_APP_API_BASE_URL}/category/list`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    const config = Object.assign(this.getConfig(token), { params: {
      c,
      q,
      s,
      n,
      o,
      categoryType
    }});
    const response = await axios.get(url, config).catch(err => {
      console.log(err);
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message, err.response?.status);
    });
    // console.log(`response.headers -> ${JSON.stringify(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME))} : ${JSON.stringify(response.headers)}`);
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCICategoryInfoRes;

  }

  /**
   * カテゴリ情報(タイプで選択する)取得
   *
   * @return {GCICategoryInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getCategorySelectInfo(categoryType?: string): Promise<GCICategoryInfoRes> {
    const url = `${process.env.VUE_APP_API_BASE_URL}/category/select`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    const config = Object.assign(this.getConfig(token), { params: {
      categoryType
    }});
    const response = await axios.get(url, config).catch(err => {
      console.log(err);
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message);
    });
    // console.log(`response.headers -> ${JSON.stringify(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME))} : ${JSON.stringify(response.headers)}`);
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCICategoryInfoRes;

  }

  /**
   * カテゴリ情報取得
   *
   * @return {GCICategoryManageListRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getCategoryManageInfo(param: SearchQueryInterface ): Promise<GCICategoryManageInfoRes> {
    const c = param.c;
    const q = param.q;
    const s = param.s;
    const n = param.n;
    const o = param.o;
    const categoryType = param.categoryType;
    const url = `${process.env.VUE_APP_API_BASE_URL}/category/manage`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    const config = Object.assign(this.getConfig(token), { params: {
      c,
      q,
      s,
      n,
      o,
      categoryType
    }});
    const response = await axios.get(url, config).catch(err => {
      console.log(err);
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message, err.response?.status);
    });
    // console.log(`response.headers -> ${JSON.stringify(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME))} : ${JSON.stringify(response.headers)}`);
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCICategoryManageInfoRes;

  }

  /**
   * コンテンツ情報更新
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {postUpsertCategoryInfo} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUpsertCategoryInfo(
    data: PUCATDataReq[]
  ): Promise<GCICategoryManageInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/category/save`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostUpsertCategoryReq(data);
      const response = await axios.post(url, req, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as GCICategoryManageInfoRes;
  }

  /**
   * コンテンツ情報取得
   *
   * @return {GCIContentsInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getContentsInfo(param: SearchQueryInterface): Promise<GCIContentsInfoRes> {
    const c = param.c;
    const q = param.q;
    const s = param.s;
    const n = param.n;
    const o = param.o;
    const url = `${process.env.VUE_APP_API_BASE_URL}/content/list`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    const config = Object.assign(this.getConfig(token), { params: {
      c,
      q,
      s,
      n,
      o
    }});
    const response = await axios.get(url, config).catch(err => {
      console.log(err);
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCIContentsInfoRes;

  }

  /**
   * コンテンツ詳細情報取得
   *
   * @return {GCIContentsListRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getContentsDetailInfo(contentId: string): Promise<GCIContentsInfoRes> {
    const url = `${process.env.VUE_APP_API_BASE_URL}/content/detail/${contentId}`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    const config = this.getConfig(token);
    const response = await axios.get(url, config).catch(err => {
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message, err.response?.status);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCIContentsInfoRes;

  }

  /**
   * コンテンツ情報検索取得
   *
   * @return {GCIContentsInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getContentsSearchInfo(param: SearchQueryInterface): Promise<GCIContentsInfoRes> {
    const c = param.c;
    const q = param.q;
    const s = param.s;
    const n = param.n;
    const o = param.o;
    const h = param.h;
    const d = param.d;
    const url = `${process.env.VUE_APP_API_BASE_URL}/content/search`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    const config = Object.assign(this.getConfig(token), { params: {
      q,
      s,
      n,
      o,
      h,
      d,
      c
    }});
    const paramsSerializer = (ps: any) => qs.stringify(ps);
    const response = await axios.get(url, {...config, paramsSerializer}).catch(err => {
      console.log(err);
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCIContentsInfoRes;

  }

  /**
   * コンテンツ情報更新
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {PostUpsertContentsRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUpsertContentsInfo(
    data: PUCDataReq
  ): Promise<PostUpsertContentsInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/content/save`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostUpsertContentsReq(new PUCDataReq(
        data.categoryId,
        data.categoryType,
        data.categoryName,
        data.contentId,
        data.title,
        data.titleNew,
        data.text,
        data.textNew,
        data.status,
        data.userId,
        data.userName,
        data.hashtag
      ));
      const response = await axios.post(url, req, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as PostUpsertContentsInfoRes;
  }

  /**
   * コンテンツ内の画像アップロード
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {PostUpsertContentsRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUploadImageContentsInfo(
    param: {
      file: File,
      contentId: string
    }
  ): Promise<PostUploadImageContentsInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/content/image`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const data = param.file;
      const contentId: string | undefined = param.contentId;
      const formData: FormData = new FormData();
      if (contentId) { formData.append('content_id', contentId); }
      formData.append('file', data, `${Math.random().toString(36).slice(-9)}_${Date.now()}`);
      const response = await axios.post(url, formData, config).catch(err => {
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as PostUploadImageContentsInfoRes;
  }

  /**
   * コンテンツ情報削除
   *
   * @param {{contentId: string}} data
   * @return {PostUpsertContentsRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postDeleteContentsInfo(
    data: PDCDataReq
  ): Promise<PostDeleteContentsInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/content/delete`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostDeleteContentsReq(new PDCDataReq(
        data.contentId
      ));
      const response = await axios.post(url, req, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as PostDeleteContentsInfoRes;
  }

  /**
   * ショップについて(About)情報取得
   *
   * @return {GCIAboutInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getAboutInfo(id: number): Promise<GCIAboutInfoRes> {
    const url = `${process.env.VUE_APP_API_BASE_URL}/about/`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    // const config = this.getConfig(token);
    const config = Object.assign(this.getConfig(token), { params: {
      id,
    }});
    const response = await axios.get(url, {...config}).catch(err => {
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message, err.response?.status);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCIAboutInfoRes;
  }

  /**
   * ショップについて(About)情報更新
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {PostUpsertContentsRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUpsertAboutInfo(
    data: PUADataReq
  ): Promise<PostUpsertAboutInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/about/save`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostUpsertAboutReq(new PUADataReq(
        data.id,
        data.title,
        data.text,
        data.status,
        data.deleted,
      ));
      const response = await axios.post(url, req, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as PostUpsertAboutInfoRes;
  }

  /**
   * ショップについて(About)の画像アップロード
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {PostUpsertAboutRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUploadImageAboutInfo(
    param: {
      file: File,
      id: string
    }
  ): Promise<PostUploadImageAboutInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/about/image`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const data = param.file;
      const id: string | undefined = param.id + '';
      const formData: FormData = new FormData();
      if (id) { formData.append('id', id); }
      formData.append('file', data, `${Math.random().toString(36).slice(-9)}_${Date.now()}`);
      const response = await axios.post(url, formData, config).catch(err => {
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as PostUploadImageAboutInfoRes;
  }

  /**
   * 見出し情報取得
   *
   * @return {GCIHeadlineInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getHeadlineInfo(id: number): Promise<GCIHeadlineInfoRes> {
    const url = `${process.env.VUE_APP_API_BASE_URL}/headline/`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    // const config = this.getConfig(token);
    const config = Object.assign(this.getConfig(token), { params: {
      id,
    }});
    const response = await axios.get(url, {...config}).catch(err => {
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message, err.response?.status);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCIHeadlineInfoRes;
  }

  /**
   * 見出し情報更新
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {PostUpsertContentsRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUpsertHeadlineInfo(
    data: PUHDataReq
  ): Promise<PostUpsertHeadlineInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/headline/save`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostUpsertHeadlineReq(new PUHDataReq(
        data.id,
        data.title,
        data.text,
        data.status,
        data.deleted,
      ));
      const response = await axios.post(url, req, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as PostUpsertHeadlineInfoRes;
  }

  /**
   * 見出し情報取得
   *
   * @return {GCIHolidayInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getHolidayInfo(id: number): Promise<GCIHolidayInfoRes> {
    const url = `${process.env.VUE_APP_API_BASE_URL}/holiday/`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    // const config = this.getConfig(token);
    const config = Object.assign(this.getConfig(token), { params: {
      id,
    }});
    const response = await axios.get(url, {...config}).catch(err => {
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message, err.response?.status);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCIHolidayInfoRes;
  }

  /**
   * 見出し情報更新
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {PostUpsertContentsRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUpsertHolidayInfo(
    data: PUHDDataReq
  ): Promise<PostUpsertHolidayInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/holiday/save`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostUpsertHolidayReq(new PUHDataReq(
        data.id,
        data.title,
        data.text,
        data.status,
        data.deleted,
      ));
      const response = await axios.post(url, req, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as PostUpsertHolidayInfoRes;
  }

  /**
   * メニュー情報取得
   *
   * @return {GCIMenuInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getMenuInfo(id: number): Promise<GCIMenuInfoRes> {
    const url = `${process.env.VUE_APP_API_BASE_URL}/menu/`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    // const config = this.getConfig(token);
    const config = Object.assign(this.getConfig(token), { params: {
      id,
    }});
    const response = await axios.get(url, {...config}).catch(err => {
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message, err.response?.status);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCIMenuInfoRes;
  }

  /**
   * メニュー情報更新
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {PostUpsertContentsRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUpsertMenuInfo(
    data: PUMDataReq
  ): Promise<PostUpsertMenuInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/menu/save`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostUpsertMenuReq(new PUMDataReq(
        data.id,
        data.title,
        data.text,
        data.status,
        data.deleted,
      ));
      const response = await axios.post(url, req, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as PostUpsertMenuInfoRes;
  }

  /**
   * インフォメーション情報取得
   *
   * @return {GCIMenuInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getInfomationInfo(id: number): Promise<GCIInfomationInfoRes> {
    const url = `${process.env.VUE_APP_API_BASE_URL}/infomation/`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    // const config = this.getConfig(token);
    const config = Object.assign(this.getConfig(token), { params: {
      id,
    }});
    const response = await axios.get(url, {...config}).catch(err => {
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message, err.response?.status);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCIInfomationInfoRes;
  }

  /**
   * インフォメーション情報更新
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {PostUpsertContentsRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUpsertInfomationInfo(
    data: PUIDataReq
  ): Promise<PostUpsertInfomationInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/infomation/save`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostUpsertInfomationReq(new PUIDataReq(
        data.id,
        data.name,
        data.address,
        data.tel,
        data.mail,
        data.fax,
        data.instagramURL,
        data.twitterURL,
        data.facebookURL,
        data.blogURL
      ));
      const response = await axios.post(url, req, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as PostUpsertInfomationInfoRes;
  }

  /**
   * インフォメーション一覧情報取得
   *
   * @return {GCIHeaderListsInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async getHeaderListsInfo(param: SearchQueryInterface): Promise<GCIHeaderListsInfoRes> {
    const c = param.c;
    const q = param.q;
    const s = param.s;
    const n = param.n;
    const o = param.o;
    const url = `${process.env.VUE_APP_API_BASE_URL}/header/list`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    const config = Object.assign(this.getConfig(token), { params: {
      c,
      q,
      s,
      n,
      o
    }});
    const response = await axios.get(url, config).catch(err => {
      console.log(err);
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCIHeaderListsInfoRes;
  }

  /**
   * インフォメーション情報取得
   *
   * @return {GCIMenuInfoRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   import {GCIHeaderRes, GCIHeaderInfoRes} from '@/model/dto/res/header/get-header-info-res';
   import {PUIHDataReq, PostUpsertHeaderReq} from '@/model/dto/req/header/post-upsert-header-req';
   */
  public async getHeaderInfo(id: number): Promise<GCIInfomationInfoRes> {
    const url = `${process.env.VUE_APP_API_BASE_URL}/header/`;
    const token = appModule.token !== '' ? appModule.token : userModule.csrfToken;
    // const config = this.getConfig(token);
    const config = Object.assign(this.getConfig(token), { params: {
      id,
    }});
    const response = await axios.get(url, {...config}).catch(err => {
      const error = err.response?.data?.error;
      throw new ApiError(error?.name, error?.message, err.response?.status);
    });
    this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
    return response.data as GCIInfomationInfoRes;
  }

  /**
   * ヘッダー情報アップロード
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {PostUpsertAboutRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUpsertHeaderInfo(
    data: PUIHDataReq
  ): Promise<GCIHeaderInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/header/save`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostUpsertHeaderReq(new PUIHDataReq(
        data.id,
        data.title,
        data.text,
        data.path,
        data.status,
        data.deleted,
      ));
      const response = await axios.post(url, req, config).catch(err => {
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as GCIHeaderInfoRes;
  }

  /**
   * ヘッダー情報の画像アップロード
   *
   * @param {{categoryId: number,contentId: string,title: string,text: string}} data
   * @return {PostUpsertAboutRes} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postUploadImageHeaderInfo(
    param: {
      file: File,
      id: string
    }
  ): Promise<PostUploadImageAboutInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/header/image`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const data = param.file;
      const id: string | undefined = param.id + '';
      const formData: FormData = new FormData();
      if (id) { formData.append('id', id); }
      formData.append('file', data, `${Math.random().toString(36).slice(-9)}_${Date.now()}`);
      const response = await axios.post(url, formData, config).catch(err => {
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as PostUploadImageAboutInfoRes;
  }

  /**
   * ヘッダー情報の画像削除
   *
   * @param {{id: string}} data
   * @return {PostUpsertHeaderReq} APIレスポンス
   * @throws {ApiError} APIに関するエラー
   *
   */
  public async postDeleteImageHeaderInfo(
    data: PUIHDataReq
  ): Promise<GCIHeaderInfoRes> {
      const url = `${process.env.VUE_APP_API_BASE_URL}/header/delete`;
      const config = Object.assign(this.getConfig(appModule.token, userModule.csrfToken), this.timeout);
      const req = new PostUpsertHeaderReq(new PUIHDataReq(
        data.id,
        data.title,
        data.text,
        data.path,
        data.status,
        data.deleted,
      ));
      const response = await axios.post(url, req, config).catch(err => {
        console.log(err);
        const error = err.response?.data?.error;
        throw new ApiError(error?.name, error?.message);
      });
      this.updateToken(response.headers.hasOwnProperty(GlobalConstants.LGCAPI.AUTH_TOKEN_NAME) ? response.headers[GlobalConstants.LGCAPI.AUTH_TOKEN_NAME] : '');
      return response.data as GCIHeaderInfoRes;
  }

}

export default new Api();
